/**
 * Ferramenta de régua (medição de distância) do mapa MapBox.
 *
 * Vive fora do MapboxDraw de propósito: a linha medida não é uma forma de busca
 * (não recorta empreendimentos, não entra na lista de formas nem nos projetos
 * salvos), então usa fonte e camadas próprias.
 *
 * Uso: clique para marcar cada ponto, duplo clique (ou Esc) para encerrar a
 * medição. Cada vértice recebe o rótulo da distância acumulada até ele.
 */

const SOURCE_ID = 'ruler';
const LINE_LAYER_ID = 'ruler-line';
const PREVIEW_LAYER_ID = 'ruler-line-preview';
const POINTS_LAYER_ID = 'ruler-points';
const LABELS_LAYER_ID = 'ruler-labels';
const RULER_LAYERS = [LINE_LAYER_ID, PREVIEW_LAYER_ID, POINTS_LAYER_ID, LABELS_LAYER_ID];
const RULER_COLOR = '#ff6b00';
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
const EARTH_RADIUS_M = 6371008.8;

const toRad = (graus) => (graus * Math.PI) / 180;

/**
 * Distância em metros entre dois pontos [lng, lat] (haversine — precisão
 * suficiente para medições dentro de uma cidade e evita mais uma dependência).
 */
const haversine = ([lng1, lat1], [lng2, lat2]) => {
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
};

export const formatDistance = (metros) => (
    metros < 1000
        ? `${Math.round(metros)} m`
        : `${(metros / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`
);

export class RulerTool {
    /**
     * @param {object} options
     * @param {mapboxgl.Map} options.map
     * @param {(estado: { active: boolean, total: number, pontos: number }) => void} [options.onChange]
     *        Notifica a UI a cada mudança (ativação, novo ponto, limpeza).
     */
    constructor({ map, onChange = null }) {
        this.map = map;
        this.onChange = onChange;
        this.active = false;
        this.points = [];
        this.hoverPoint = null;
    }

    // ===== ciclo de vida =====

    toggle = () => {
        if (this.active) {
            this.deactivate();
        } else {
            this.activate();
        }
    };

    activate = () => {
        if (!this.map || this.active) return;

        this.active = true;
        this.points = [];
        this.hoverPoint = null;
        this.setupLayers();

        this.map.getCanvas().style.cursor = 'crosshair';
        // Sem isso o duplo clique que encerra a medição também dá zoom no mapa.
        this.map.doubleClickZoom.disable();
        this.map.on('click', this.handleClick);
        this.map.on('dblclick', this.handleDoubleClick);
        this.map.on('mousemove', this.handleMouseMove);
        document.addEventListener('keydown', this.handleKeyDown);

        this.render();
    };

    /** Encerra a medição e apaga o traçado. */
    deactivate = () => {
        if (!this.map) return;

        this.active = false;
        this.points = [];
        this.hoverPoint = null;

        this.map.getCanvas().style.cursor = '';
        this.map.doubleClickZoom.enable();
        this.map.off('click', this.handleClick);
        this.map.off('dblclick', this.handleDoubleClick);
        this.map.off('mousemove', this.handleMouseMove);
        document.removeEventListener('keydown', this.handleKeyDown);

        this.render();
    };

    /** Recria fonte e camadas — necessário após troca de estilo do mapa. */
    setupLayers = () => {
        if (!this.map.getSource(SOURCE_ID)) {
            this.map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
        }

        // Linha medida e prévia ficam em camadas separadas porque `line-dasharray`
        // não aceita expressão por feature.
        if (!this.map.getLayer(LINE_LAYER_ID)) {
            this.map.addLayer({
                id: LINE_LAYER_ID,
                type: 'line',
                source: SOURCE_ID,
                filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!', ['get', 'preview']]],
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': RULER_COLOR, 'line-width': 2.5 },
            });
        }

        // Segmento tracejado que acompanha o cursor até o próximo clique.
        if (!this.map.getLayer(PREVIEW_LAYER_ID)) {
            this.map.addLayer({
                id: PREVIEW_LAYER_ID,
                type: 'line',
                source: SOURCE_ID,
                filter: ['all', ['==', ['geometry-type'], 'LineString'], ['get', 'preview']],
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': RULER_COLOR, 'line-width': 2, 'line-dasharray': [2, 2] },
            });
        }

        if (!this.map.getLayer(POINTS_LAYER_ID)) {
            this.map.addLayer({
                id: POINTS_LAYER_ID,
                type: 'circle',
                source: SOURCE_ID,
                filter: ['==', ['geometry-type'], 'Point'],
                paint: {
                    'circle-radius': 5,
                    'circle-color': '#ffffff',
                    'circle-stroke-width': 2.5,
                    'circle-stroke-color': RULER_COLOR,
                },
            });
        }

        if (!this.map.getLayer(LABELS_LAYER_ID)) {
            this.map.addLayer({
                id: LABELS_LAYER_ID,
                type: 'symbol',
                source: SOURCE_ID,
                filter: ['==', ['geometry-type'], 'Point'],
                layout: {
                    'text-field': ['get', 'label'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12,
                    'text-offset': [0, -1.2],
                    'text-allow-overlap': true,
                },
                paint: {
                    'text-color': RULER_COLOR,
                    'text-halo-color': '#ffffff',
                    'text-halo-width': 1.5,
                },
            });
        }
    };

    /** Reaplica o traçado depois de uma troca de estilo (que descarta as camadas). */
    reapplyAfterStyleChange = () => {
        if (!this.active) return;
        this.setupLayers();
        this.render();
    };

    // ===== interação =====

    handleClick = (e) => {
        this.points.push([e.lngLat.lng, e.lngLat.lat]);
        this.render();
    };

    // Encerra a medição mantendo o traçado na tela; o clique que compõe o duplo
    // clique já adicionou o ponto final, então nada é acrescentado aqui.
    handleDoubleClick = (e) => {
        e.preventDefault();
        this.finish();
    };

    handleMouseMove = (e) => {
        if (this.points.length === 0) return;
        this.hoverPoint = [e.lngLat.lng, e.lngLat.lat];
        // Sem `notify`: o total não muda com o cursor e a UI não precisa
        // re-renderizar a cada movimento do mouse.
        this.render({ notify: false });
    };

    handleKeyDown = (e) => {
        if (e.key === 'Escape') this.deactivate();
    };

    /**
     * Para de capturar cliques, mas deixa a medição visível no mapa até o
     * usuário limpar (botão da régua novamente).
     */
    finish = () => {
        if (!this.active) return;

        this.map.getCanvas().style.cursor = '';
        this.map.doubleClickZoom.enable();
        this.map.off('click', this.handleClick);
        this.map.off('dblclick', this.handleDoubleClick);
        this.map.off('mousemove', this.handleMouseMove);

        this.hoverPoint = null;
        this.render();
    };

    // ===== render =====

    /** Distância acumulada até cada ponto, em metros. */
    cumulativeDistances = () => this.points.reduce((acc, ponto, i) => {
        acc.push(i === 0 ? 0 : acc[i - 1] + haversine(this.points[i - 1], ponto));
        return acc;
    }, []);

    total = () => {
        const acumuladas = this.cumulativeDistances();
        return acumuladas.length ? acumuladas[acumuladas.length - 1] : 0;
    };

    render = ({ notify = true } = {}) => {
        const source = this.map.getSource(SOURCE_ID);
        if (!source) return;

        const acumuladas = this.cumulativeDistances();
        const features = this.points.map((ponto, i) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: ponto },
            properties: { label: i === 0 ? 'Início' : formatDistance(acumuladas[i]) },
        }));

        if (this.points.length > 1) {
            features.push({
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: this.points },
                properties: { preview: false },
            });
        }

        if (this.hoverPoint && this.points.length > 0) {
            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [this.points[this.points.length - 1], this.hoverPoint],
                },
                properties: { preview: true },
            });
        }

        source.setData({ type: 'FeatureCollection', features });

        if (notify) {
            this.onChange?.({
                active: this.active,
                total: this.total(),
                pontos: this.points.length,
            });
        }
    };

    destroy = () => {
        this.deactivate();
        RULER_LAYERS.forEach((id) => {
            if (this.map?.getLayer(id)) this.map.removeLayer(id);
        });
        if (this.map?.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
    };
}

export { RULER_LAYERS, SOURCE_ID as RULER_SOURCE_ID };
